import React, { useId, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';
import { useEffectiveAddons } from '../../contexts/effective-features/EffectiveFeaturesContext';
import { usePromptGroups } from '../../hooks/prompt/UsePromptGroups';
import { useGetMonitorsForProject } from '../../api/hooks/monitor/useGetMonitorsForProject';
import usePromptGroupId, { ALL_PROMPT_GROUPS_ID } from '../../hooks/filters/UsePromptGroupId';
import './PromptGroupDropdown.scss';

export interface PromptGroupDropdownProps {
  projectId: number;
}

const PromptGroupDropdown: React.FC<PromptGroupDropdownProps> = ({ projectId }) => {
  const { t } = useTranslation();
  // CSS ID selectors reject colons; React useId() emits them (e.g. `:r1:`).
  const idPrefix = useId().replace(/:/g, '');
  const { promptGroupId, setPromptGroupId } = usePromptGroupId();
  const effectiveAddons = useEffectiveAddons();
  const hasPromptGroupsAddon = effectiveAddons.includes('PROMPT_GROUP');
  const { data: promptGroups } = usePromptGroups(projectId);
  const { monitorPrompts } = useGetMonitorsForProject(projectId);

  const options = useMemo(() => {
    if (!hasPromptGroupsAddon || !promptGroups || !monitorPrompts) {
      return [];
    }
    const groupIdsWithPrompts = new Set(
      monitorPrompts.map((p) => p.promptGroupId).filter((id): id is number => id != null)
    );
    const nonEmpty = promptGroups.filter((g) => groupIdsWithPrompts.has(g.id));
    if (nonEmpty.length === 0) {
      return [];
    }
    return [
      { label: t('allGroups'), value: ALL_PROMPT_GROUPS_ID },
      ...nonEmpty.map((g) => ({ label: g.name, value: g.id })),
    ];
  }, [hasPromptGroupsAddon, promptGroups, monitorPrompts, t]);

  if (options.length === 0) {
    return null;
  }

  return (
    <Dropdown
      value={promptGroupId}
      onChange={(e) => setPromptGroupId(e.value)}
      options={options}
      optionLabel="label"
      optionValue="value"
      className="dropdown dropdown--group-filter"
      valueTemplate={(option) => (
        <>
          <span id={`${idPrefix}-val-${option?.value}`} className="dropdown--group-filter__label">
            {option?.label}
          </span>
          {option?.label && (
            <Tooltip
              target={`#${idPrefix}-val-${option.value}`}
              content={option.label}
              position="top"
              showDelay={300}
            />
          )}
        </>
      )}
      itemTemplate={(option) => (
        <>
          <span id={`${idPrefix}-item-${option.value}`} className="dropdown--group-filter__label">
            {option.label}
          </span>
          <Tooltip
            target={`#${idPrefix}-item-${option.value}`}
            content={option.label}
            position="top"
            showDelay={300}
          />
        </>
      )}
    />
  );
};

export default PromptGroupDropdown;
